// @ts-strict-ignore
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ResizingStage } from 'rainbow-web-sdk/services/virtualBackground/pipeline/webgl2/resizingStage';
import { SoftMax } from 'rainbow-web-sdk/services/virtualBackground/pipeline/webgl2/softMax';
import { JointBilateralFilter } from 'rainbow-web-sdk/services/virtualBackground/pipeline/webgl2/jointBilateralFilter';
import { BlurStage } from 'rainbow-web-sdk/services/virtualBackground/pipeline/webgl2/blurStage';
import { ImageStage } from 'rainbow-web-sdk/services/virtualBackground/pipeline/webgl2/imageStage';
import { IVideoPipeline, VIRTUAL_BACKGROUND_TYPE } from 'rainbow-web-sdk/services/virtualBackground/virtualBackground.service';
import { WebglHelper } from 'rainbow-web-sdk/services/virtualBackground/pipeline/webgl2/webglHelper';

interface PostProcessingConfig {
    smoothSegmentation: boolean
    jointBilateralFilter: JointBilateralFilterConfig
    coverage: [number, number]
    lightWrapping: number
    blendMode: number
}

interface JointBilateralFilterConfig {
    sigmaSpace: number
    sigmaColor: number
}

export const glsl = String.raw;

export const segmentationDimensions = {
    model96: { height: 96, width: 160 },
    model144: { height: 144, width: 256 }
};

export class Webgl2Pipeline implements IVideoPipeline {
    private logger: LoggerService = LoggerService.getInstance();
    public options: any;
    public segmentationPixelCount: number;
    public tflite: any;
    public gl: WebGL2RenderingContext;
    public segmentationMaskCanvas: HTMLCanvasElement;
    public segmentationTexture: WebGLTexture;
    public personMaskTexture: WebGLTexture;
    private virtualBackgroundResizingStage: ResizingStage;
    private virtualBackgroundSoftMax: SoftMax;
    private virtualBackgroundJointBilateralFilter: JointBilateralFilter;
    private virtualBackgroundBlurStage: BlurStage;
    private virtualBackgroundImageStage: ImageStage;
    private vbService: any;
    private vbHelper: WebglHelper;
    public VIRTUAL_BACKGROUND_TYPE: any;

    public maskFrameTimerWorker: Worker;
    public virtualImage: CanvasImageSource;
    public positionBuffer: WebGLBuffer;
    private vertexArray: WebGLVertexArrayObject;
    private inputFrameTexture: WebGLTexture;
    public texCoordBuffer: WebGLBuffer;
    public vertexShader: WebGLShader;
    public outputMemoryOffset: number;
    public outputCanvasElement: HTMLCanvasElement = null;
    public statsEnabled: boolean;
    public stats: any;

    public static create(virtualBackgroundService: any): Webgl2Pipeline {
        return new Webgl2Pipeline(virtualBackgroundService);
    }

    private constructor(virtualBackgroundService: any) {
        this.vbService = virtualBackgroundService;
        this.vbService.setPostProcessingValue();
    }

    /**
     * Initialization of the different methods and components used to create the virtual background
     */
    public async init(): Promise<any> {
        try {
            this.tflite = this.vbService.tflite;
            this.outputCanvasElement = this.vbService.outputCanvasElement;
            this.gl = this.vbService.gl;
            this.segmentationPixelCount = this.vbService.segmentationPixelCount;
            this.VIRTUAL_BACKGROUND_TYPE = VIRTUAL_BACKGROUND_TYPE;
            this.statsEnabled = this.vbService.statsEnabledFlag;
            this.stats = this.vbService.stats;
            this.vbHelper = WebglHelper.create();
            const vertexShaderSource = glsl`#version 300 es

            in vec2 a_position;
            in vec2 a_texCoord;

            // a varying to pass the texture coordinates to the fragment shader
            out vec2 v_texCoord;

            void main(){
                //Recovers the position of the current vertex (with a projection on the plane)
                gl_Position = vec4(a_position, 0.0, 1.0);

                // Pass the texcoord to the fragment shader.
                v_texCoord = a_texCoord;
            }`;


            // Create the main vertex shader (projection on plan)
            this.vertexShader = this.vbHelper.initShader(this.gl, this.gl.VERTEX_SHADER, vertexShaderSource);

            this.vertexArray = this.gl.createVertexArray();
            this.gl.bindVertexArray(this.vertexArray);

            // Configuration of the canvas coordinates (the coordinates of the 4 corners of the canvas)
            this.positionBuffer = this.gl.createBuffer();
            this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer);
            this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array([-1.0, -1.0, 1.0, -1.0, -1.0, 1.0, 1.0, 1.0]), this.gl.STATIC_DRAW);

            // Configuration of the texture coordinates (the coordinates of the 4 corners of the canvas to have the whole virtual image)
            this.texCoordBuffer = this.gl.createBuffer();
            this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.texCoordBuffer);
            this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array([0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 1.0, 1.0]), this.gl.STATIC_DRAW);

            // Creation of the texture that will receive the video stream
            this.inputFrameTexture = this.gl.createTexture();
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.inputFrameTexture);
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);

            // creation of the texture for the mask of the person
            this.personMaskTexture = this.vbHelper.createTexture(this.gl, this.gl.RGBA8, this.vbService.videoElement.width, this.vbService.videoElement.height);

            this.options = this.vbService.options;
            // Create the segmentation mask context
            this.segmentationMaskCanvas = document.createElement('canvas');
            this.segmentationMaskCanvas.width = this.options.segmentationDimensions.width;
            this.segmentationMaskCanvas.height = this.options.segmentationDimensions.height;
            this.segmentationTexture = this.vbHelper.createTexture(this.gl, this.gl.RGBA8, this.segmentationMaskCanvas.width, this.segmentationMaskCanvas.height);

            // Initialize stages
            this.virtualBackgroundResizingStage = ResizingStage.create(this);
            this.virtualBackgroundSoftMax = SoftMax.create(this);
            this.virtualBackgroundJointBilateralFilter = JointBilateralFilter.create(this);
            this.virtualBackgroundImageStage = await ImageStage.create(this.options, this);
            this.virtualBackgroundBlurStage = BlurStage.create(this.options, this);

            this.updatePostProcessingConfig();

            this.gl.clearColor(0, 0, 0, 0);
            this.gl.clear(this.gl.COLOR_BUFFER_BIT);

        }
        catch (error) {
            this.vbService.setBackgroundMode('blur', null);
            this.logger.error(`[VirtualBackgroundWebGL2Pipeline] startEffect -- Failure -- ${error.message}`);
        }

    }

    /**
     * Calls the different stages that make up the virtual background
     */
    public async render() {
        try {
            if (this.vbService.statsEnabledFlag) {
                const t0 = performance.now();

                // Declare current active texture (TEXTURE0)
                this.gl.activeTexture(this.gl.TEXTURE0);
                this.gl.bindTexture(this.gl.TEXTURE_2D, this.inputFrameTexture);

                // Attach video to current active texture
                this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, this.vbService.videoElement);

                // We put ourselves in the context of the vertexArray (to get the coordinates of the canvas)
                this.gl.bindVertexArray(this.vertexArray);

                const t1 = performance.now();
                // Resize video frame by GPU
                await this.virtualBackgroundResizingStage.render();

                const t2 = performance.now();
                // Compute person mask by CPU
                this.tflite._runInference();

                const t3 = performance.now();
                // Apply softmax filter on tflite results by GPU
                this.virtualBackgroundSoftMax.render();

                const t4 = performance.now();

                // Apply jointBilateralFilter on texture segmentation mask by GPU
                this.virtualBackgroundJointBilateralFilter.render();
                const t5 = performance.now();

                if (this.virtualBackgroundImageStage) this.virtualBackgroundImageStage.render();
                else if (this.virtualBackgroundBlurStage) this.virtualBackgroundBlurStage.render();

                const t6 = performance.now();

                this.vbService.stats.value.fps++;
                this.vbService.stats.value.webglinitialization += t1 - t0;
                this.vbService.stats.value.resizingstage += t2 - t1;
                this.vbService.stats.value.tflite += t3 - t2;
                this.vbService.stats.value.softMax += t4 - t3;
                this.vbService.stats.value.jointBilateralFilter += t5 - t4;
                this.vbService.stats.value.realization += t6 - t5;
            }
            else {
                // Declare current active texture (TEXTURE0)
                this.gl.activeTexture(this.gl.TEXTURE0);
                this.gl.bindTexture(this.gl.TEXTURE_2D, this.inputFrameTexture);

                // Attach video to current active texture
                this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, this.vbService.videoElement);

                // We put ourselves in the context of the vertexArray (to get the coordinates of the canvas)
                this.gl.bindVertexArray(this.vertexArray);

                // Resize video frame by GPU
                await this.virtualBackgroundResizingStage.render();

                // Compute person mask by CPU
                this.tflite._runInference();

                // Apply softmax filter on tflite results by GPU
                this.virtualBackgroundSoftMax.render();

                // Apply jointBilateralFilter on texture segmentation mask by GPU
                this.virtualBackgroundJointBilateralFilter.render();

                if (this.virtualBackgroundImageStage) this.virtualBackgroundImageStage.render();
                else if (this.virtualBackgroundBlurStage) this.virtualBackgroundBlurStage.render();
            }
        }

        catch (error) {
            this.logger.error(`[VirtualBackgroudWebgl2Pipeline] render -- failure -- ${error.message}`);
        }
    }

    /**
     * Initialization of the different default settings
     */
    public updatePostProcessingConfig() {
        // Default configuration of virtual background processing
        const postProcessingConfig: PostProcessingConfig = {
            smoothSegmentation: true,
            jointBilateralFilter: { sigmaColor: this.vbService.sigmaColor, sigmaSpace: this.vbService.sigmaSpace },
            coverage: [this.vbService.coverageMin, this.vbService.coverageMax],
            lightWrapping: this.vbService.lightWrapping,
            blendMode: this.vbService.blendMode
        };

        this.virtualBackgroundJointBilateralFilter.updateSigmaSpace(postProcessingConfig.jointBilateralFilter.sigmaSpace);
        this.virtualBackgroundJointBilateralFilter.updateSigmaColor(postProcessingConfig.jointBilateralFilter.sigmaColor);

        if (this.virtualBackgroundImageStage) {
            this.virtualBackgroundImageStage.updateCoverage(postProcessingConfig.coverage);
            this.virtualBackgroundImageStage.updateLightWrapping(postProcessingConfig.lightWrapping);
            this.virtualBackgroundImageStage.updateBlendMode(postProcessingConfig.blendMode);
        }
        else if (this.virtualBackgroundBlurStage) {
            this.virtualBackgroundBlurStage.updateCoverage(postProcessingConfig.coverage);
        }
        else {
            this.virtualBackgroundImageStage.updateCoverage([0, 0.9999]);
            this.virtualBackgroundImageStage.updateLightWrapping(0);
        }
    }

    /**
     * Removes the different components used to create the virtual background
     */
    public cleanUp() {
        if (this.virtualBackgroundImageStage) this.virtualBackgroundImageStage.cleanUp();
        if (this.virtualBackgroundJointBilateralFilter) this.virtualBackgroundJointBilateralFilter.cleanUp();
        if (this.virtualBackgroundSoftMax) this.virtualBackgroundSoftMax.cleanUp();
        if (this.virtualBackgroundResizingStage) this.virtualBackgroundResizingStage.cleanUp();
        if (this.virtualBackgroundBlurStage) this.virtualBackgroundBlurStage.cleanUp();
        if (this.segmentationMaskCanvas) this.segmentationMaskCanvas.remove();
        if (this.personMaskTexture) this.gl.deleteTexture(this.personMaskTexture);
        if (this.segmentationTexture) this.gl.deleteTexture(this.segmentationTexture);
        if (this.inputFrameTexture) this.gl.deleteTexture(this.inputFrameTexture);
        if (this.texCoordBuffer) this.gl.deleteBuffer(this.texCoordBuffer);
        if (this.positionBuffer) this.gl.deleteBuffer(this.positionBuffer);
        if (this.vertexArray) this.gl.deleteVertexArray(this.vertexArray);
        if (this.vertexShader) this.gl.deleteShader(this.vertexShader);
    }

}
